<template>
  <main class="admin-page">
    <div class="admin-shell">
      <MarketingToolHeader
        eyebrow="Akademie Verwaltung"
        title="Administration"
        description="Verwalte Lernmodule, Inhalte, Schulungen und Reports in einem zentralen Arbeitsbereich."
        icon="mdi-shield-crown-outline">
        <template v-slot:actions>
          <v-btn icon class="icon-action academy-local-back" aria-label="Zurück zur Akademie" title="Zurück zur Akademie" to="/akademie">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="admin-badge"><v-icon>mdi-lock-outline</v-icon>Admin-Bereich</span>
        </template>
      </MarketingToolHeader>

      <nav class="admin-nav" aria-label="Administrationsbereiche">
        <button
          v-for="(item, index) in navItems"
          :key="item.label"
          type="button"
          :class="{ active: adminTab === index }"
          :aria-current="adminTab === index ? 'page' : null"
          @click="adminTab = index">
          <span class="nav-icon"
            ><v-icon>{{ item.icon }}</v-icon></span
          >
          <div>
            <strong>{{ item.label }}</strong
            ><small>{{ item.description }}</small>
          </div>
          <v-icon class="nav-arrow">mdi-chevron-right</v-icon>
        </button>
      </nav>

      <v-tabs-items v-model="adminTab" dark class="admin-content">
        <v-tab-item>
          <section class="admin-surface"><ModuleManager /></section>
        </v-tab-item>
        <v-tab-item>
          <section class="admin-surface"><TerminManager /></section>
        </v-tab-item>
        <v-tab-item>
          <section class="admin-surface report-surface"><LicenseOverview /></section>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </main>
</template>

<script>
import ModuleManager from "./ModuleManager.vue"
import TerminManager from "./TerminManager.vue"
import LicenseOverview from "../reports/LicenseOverview.vue"
import MarketingToolHeader from "@/views/dashboard/communication/MarketingToolHeader.vue"

export default {
  name: "AkademieAdmin",
  components: { ModuleManager, TerminManager, LicenseOverview, MarketingToolHeader },
  data() {
    return {
      adminTab: 0,
      navItems: [
        {
          label: "Module & Lektionen",
          description: "Lerninhalte pflegen",
          icon: "mdi-book-open-page-variant-outline",
        },
        { label: "Schulungen", description: "Termine & Anmeldungen", icon: "mdi-calendar-star-outline" },
        { label: "Reports", description: "Lizenzen auswerten", icon: "mdi-chart-box-outline" },
      ],
    }
  },
}
</script>

<style scoped>
.admin-page {
  --surface: rgba(255, 255, 255, 0.035);
  --border: rgba(255, 255, 255, 0.1);
  --text: #f4f4f5;
  --muted: #a3a3a8;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 1.5rem;
  color: var(--text);
  background: radial-gradient(circle at 15% -5%, rgba(0, 150, 64, 0.13), transparent 28rem), #121212;
  font-size: 1rem;
}

.admin-shell {
  width: 100%;
  max-width: 92.5rem;
  margin: 0 auto;
}

.icon-action {
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  border: 1px solid var(--border);
  border-radius: 0.8125rem !important;
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.admin-badge {
  display: inline-flex;
  min-height: 2.75rem;
  align-items: center;
  gap: 0.4375rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.8125rem;
  color: #ff9aa4;
  background: rgba(244, 63, 94, 0.13);
  font-size: 0.875rem;
  font-weight: 750;
}

.admin-badge .v-icon {
  color: currentColor;
}

.admin-nav {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.625rem;
  margin: 1rem 0;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 1.25rem;
  background: rgba(255, 255, 255, 0.026);
}

.admin-nav button {
  display: grid;
  min-height: 4.5rem;
  grid-template-columns: 2.625rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid transparent;
  border-radius: 0.9375rem;
  outline: none;
  color: #d4d4d8;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.admin-nav button.active {
  border-color: rgba(0, 150, 64, 0.28);
  color: #ffffff;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.13), rgba(255, 255, 255, 0.035));
}

.admin-nav button:focus-visible {
  box-shadow: 0 0 0 3px rgba(0, 150, 64, 0.13);
}

.admin-nav .nav-icon {
  display: grid;
  width: 2.625rem;
  height: 2.625rem;
  place-items: center;
  border-radius: 0.8125rem;
  color: #a3a3a8;
  background: rgba(255, 255, 255, 0.055);
}

.admin-nav button.active .nav-icon {
  color: #58d388;
  background: rgba(0, 150, 64, 0.13);
}

.admin-nav .nav-icon .v-icon {
  color: currentColor;
}

.admin-nav button > div {
  display: grid;
  min-width: 0;
}

.admin-nav strong {
  font-size: 0.875rem;
  line-height: 1.35;
}

.admin-nav small {
  margin-top: 0.125rem;
  color: var(--muted);
  font-size: 0.75rem;
}

.nav-arrow {
  color: #8f9095 !important;
}

.admin-content {
  overflow: visible;
  background: transparent !important;
}

.admin-surface {
  padding: 1.25rem;
  border: 1px solid var(--border);
  border-radius: 1.375rem;
  background: rgba(255, 255, 255, 0.03);
  box-shadow: 0 1rem 2.75rem rgba(0, 0, 0, 0.12);
}

.report-surface {
  padding: 0;
  overflow: hidden;
}

.admin-surface ::v-deep .gold-text {
  color: #58d388 !important;
}

@media (max-width: 78.9375rem) {
  .admin-page {
    padding: 1.125rem;
  }
}

@media (max-width: 59.9375rem) {
  .admin-nav {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .admin-nav button {
    min-width: 0;
  }
}

@media (max-width: 37.4375rem) {
  .admin-page {
    padding: 0.75rem;
  }

  .admin-nav {
    margin: 0.75rem 0;
    gap: 0.5rem;
    padding: 0.5rem;
    border-radius: 1.0625rem;
  }

  .admin-nav button {
    min-height: 5.25rem;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: 2.25rem auto;
    align-content: center;
    align-items: center;
    justify-items: center;
    gap: 0.375rem;
    padding: 0.625rem;
    border-color: rgba(255, 255, 255, 0.055);
    background: rgba(255, 255, 255, 0.025);
    text-align: center;
  }

  .admin-nav .nav-icon {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.6875rem;
  }

  .admin-nav button > div {
    width: 100%;
    justify-items: center;
  }

  .admin-nav strong {
    width: 100%;
    line-height: 1.25;
    overflow-wrap: anywhere;
    text-align: center;
  }

  .admin-nav small,
  .admin-nav .nav-arrow {
    display: none;
  }

  .admin-surface {
    padding: 0.75rem;
    border-radius: 1.0625rem;
  }
}
</style>
